import React, { useEffect, useState, useCallback } from 'react';
import { commentService } from '../api';
import './CommentSection.css';

const ThumbUp = ({ active }) => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill={active ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
        <path d="M7 10v12" /><path d="M15 5.88 14 10h6.29a2 2 0 0 1 1.94 2.5l-2.34 9A2 2 0 0 1 17.94 23H10a2 2 0 0 1-2-2v-9.5a2 2 0 0 1 .5-1.32L13 5a2 2 0 0 1 2 .88Z" />
    </svg>
);

const ThumbDown = ({ active }) => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill={active ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" style={{ transform: 'rotate(180deg)' }}>
        <path d="M7 10v12" /><path d="M15 5.88 14 10h6.29a2 2 0 0 1 1.94 2.5l-2.34 9A2 2 0 0 1 17.94 23H10a2 2 0 0 1-2-2v-9.5a2 2 0 0 1 .5-1.32L13 5a2 2 0 0 1 2 .88Z" />
    </svg>
);

const CommentForm = ({ onSubmit, placeholder = 'Написать комментарий...', autoFocus = false, onCancel }) => {
    const [value, setValue] = useState('');
    const [busy, setBusy] = useState(false);

    const submit = async () => {
        if (!value.trim() || busy) return;
        setBusy(true);
        try {
            await onSubmit(value.trim());
            setValue('');
            if (onCancel) onCancel();
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="comment-section__form">
            <textarea
                className="comment-section__textarea"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder={placeholder}
                rows={2}
                autoFocus={autoFocus}
            />
            <div className="comment-section__form-actions">
                {onCancel && (
                    <button className="comment-section__cancel-btn" onClick={onCancel}>Отмена</button>
                )}
                <button className="comment-section__submit-btn" onClick={submit} disabled={busy || !value.trim()}>
                    {busy ? 'Отправка...' : 'Отправить'}
                </button>
            </div>
        </div>
    );
};

const CommentItem = ({ comment, user, onReply, onRate, onRequireAuth }) => {
    const [replyOpen, setReplyOpen] = useState(false);
    const [rateBusy, setRateBusy] = useState(false);

    const handleRate = async (type) => {
        if (!user) { onRequireAuth(); return; }
        if (rateBusy) return;
        setRateBusy(true);
        try {
            const next = comment.userRating === type ? 'none' : type;
            await onRate(comment.id, next);
        } finally {
            setRateBusy(false);
        }
    };

    const initial = (comment.authorName || '?').charAt(0).toUpperCase();

    return (
        <div className="comment-section__item">
            <div className="comment-section__avatar">
                {comment.authorAvatar
                    ? <img src={comment.authorAvatar} alt={comment.authorName} />
                    : initial
                }
            </div>
            <div className="comment-section__item-body">
                <div className="comment-section__item-header">
                    <span className="comment-section__author">{comment.authorName}</span>
                    <span className="comment-section__date">
                        {new Date(comment.createdAt).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    </span>
                </div>
                <p className="comment-section__content">{comment.content}</p>
                <div className="comment-section__item-actions">
                    <button
                        className={`comment-section__rate-btn${comment.userRating === 'like' ? ' active' : ''}`}
                        onClick={() => handleRate('like')}
                    >
                        <ThumbUp active={comment.userRating === 'like'} /> {comment.likesCount > 0 && comment.likesCount}
                    </button>
                    <button
                        className={`comment-section__rate-btn${comment.userRating === 'dislike' ? ' active' : ''}`}
                        onClick={() => handleRate('dislike')}
                    >
                        <ThumbDown active={comment.userRating === 'dislike'} /> {comment.dislikesCount > 0 && comment.dislikesCount}
                    </button>
                    <button
                        className="comment-section__reply-btn"
                        onClick={() => { if (!user) { onRequireAuth(); return; } setReplyOpen(o => !o); }}
                    >
                        Ответить
                    </button>
                </div>

                {replyOpen && (
                    <CommentForm
                        placeholder={`Ответить ${comment.authorName}...`}
                        autoFocus
                        onCancel={() => setReplyOpen(false)}
                        onSubmit={async (text) => { await onReply(comment.id, text); setReplyOpen(false); }}
                    />
                )}

                {comment.replies?.length > 0 && (
                    <div className="comment-section__replies">
                        {comment.replies.map(reply => (
                            <CommentItem
                                key={reply.id}
                                comment={reply}
                                user={user}
                                onReply={onReply}
                                onRate={onRate}
                                onRequireAuth={onRequireAuth}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

const CommentSection = ({ adId, user, onRequireAuth }) => {
    const [comments, setComments] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [sortBy, setSortBy] = useState('new');
    const [loading, setLoading] = useState(true);

    const loadComments = useCallback(async (targetPage, targetSort, append) => {
        setLoading(true);
        try {
            const { data } = await commentService.getComments(adId, targetPage, targetSort);
            setComments(prev => append ? [...prev, ...data.comments] : data.comments);
            setTotalCount(data.totalCount);
            setHasMore(data.hasMore);
            setPage(targetPage);
        } finally {
            setLoading(false);
        }
    }, [adId]);

    useEffect(() => {
        if (adId) loadComments(1, sortBy, false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [adId, sortBy]);

    const handleCreate = async (content, parentCommentId = null) => {
        await commentService.createComment(adId, content, parentCommentId);
        await loadComments(1, sortBy, false);
    };

    const handleReply = async (parentCommentId, content) => {
        await commentService.createComment(adId, content, parentCommentId);
        await loadComments(1, sortBy, false);
    };

    const handleRate = async (commentId, type) => {
        // optimistic update
        const applyRating = (list) => list.map(c => {
            if (c.id === commentId) {
                const wasLike = c.userRating === 'like';
                const wasDislike = c.userRating === 'dislike';
                let likesCount = c.likesCount - (wasLike ? 1 : 0);
                let dislikesCount = c.dislikesCount - (wasDislike ? 1 : 0);
                if (type === 'like') likesCount += 1;
                if (type === 'dislike') dislikesCount += 1;
                return { ...c, userRating: type === 'none' ? null : type, likesCount, dislikesCount };
            }
            if (c.replies?.length) return { ...c, replies: applyRating(c.replies) };
            return c;
        });
        setComments(prev => applyRating(prev));
        try {
            await commentService.rateComment(adId, commentId, type);
        } catch {
            loadComments(page, sortBy, false);
        }
    };

    return (
        <div className="listing-detail__section comment-section">
            <div className="comment-section__header">
                <h2 className="listing-detail__section-title">Комментарии{totalCount > 0 ? ` (${totalCount})` : ''}</h2>
                <select className="comment-section__sort" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                    <option value="new">Сначала новые</option>
                    <option value="old">Сначала старые</option>
                    <option value="popular">Популярные</option>
                </select>
            </div>

            {user ? (
                <CommentForm onSubmit={(text) => handleCreate(text)} />
            ) : (
                <button className="comment-section__login-prompt" onClick={onRequireAuth}>
                    Войдите, чтобы оставить комментарий
                </button>
            )}

            <div className="comment-section__list">
                {comments.map(comment => (
                    <CommentItem
                        key={comment.id}
                        comment={comment}
                        user={user}
                        onReply={handleReply}
                        onRate={handleRate}
                        onRequireAuth={onRequireAuth}
                    />
                ))}
                {!loading && comments.length === 0 && (
                    <p className="comment-section__empty">Пока нет комментариев. Будьте первым!</p>
                )}
            </div>

            {hasMore && (
                <button className="comment-section__load-more" onClick={() => loadComments(page + 1, sortBy, true)} disabled={loading}>
                    {loading ? 'Загрузка...' : 'Показать ещё'}
                </button>
            )}
        </div>
    );
};

export default CommentSection;
